import {useRef, useEffect, useState} from 'react'
import BlurBg from './assets/hero/hero-blur-bg.webp'
import MonaHead from './assets/hero/mona-head.webp'
import Clouds1 from './assets/hero/clouds1.webp'
import Clouds2 from './assets/hero/clouds2.webp'
import HeroButterfly from './components/Butterfly/HeroButterfly'

import PearlCloud from './components/PearlCloud/PearlCloud'
// import {isFeatureEnabled} from '@github-ui/feature-flags'

export default function IntroHeroUniverse({
  setCopyScrollOpacity,
  setCopyScrollScale,
  isAssetsLoaded,
}: {
  setCopyScrollOpacity: (o: number) => void
  setCopyScrollScale: (o: number) => void
  isAssetsLoaded: boolean
}) {
  const animationFrameId = useRef(0)
  const introVisualsRef = useRef<HTMLDivElement | null>(null)
  const monaHeadRef = useRef<HTMLDivElement | null>(null)
  const clouds1Ref = useRef<HTMLDivElement | null>(null)
  const clouds2Ref = useRef<HTMLDivElement | null>(null)
  const blurBgRef = useRef<HTMLDivElement | null>(null)
  const [animTrigger, setAnimTrigger] = useState(false)

  // Image loading state - only the 4 images actually used
  const [, setLoadedImages] = useState(() => new Set<string>())

  // Only the necessary images used in this component
  const imageUrls = [BlurBg, MonaHead, Clouds1, Clouds2]

  for (const url of imageUrls) {
    const img = new Image()
    img.src = url
    img.onload = () => handleImageLoad(url)
  }

  const handleImageLoad = (imageUrl: string) => {
    setLoadedImages(prev => {
      const newSet = new Set(prev)
      newSet.add(imageUrl)

      // Check if all images are loaded
      if (newSet.size === imageUrls.length) {
        if (introVisualsRef.current) {
          introVisualsRef.current.classList.add('images-loaded')
        }
      }

      return newSet
    })
  }

  useEffect(() => {
    let isReducedMotion = false

    const handleReduceMotionChange = (event: MediaQueryListEvent) => {
      isReducedMotion = event.matches

      if (isReducedMotion) {
        if (introVisualsRef.current) {
          introVisualsRef.current.classList.add('reduced-motion')
        }
        setCopyScrollScale(1)
        setCopyScrollOpacity(1)
      } else {
        if (introVisualsRef.current) {
          introVisualsRef.current.classList.remove('reduced-motion')
        }
      }
    }

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    mediaQuery.addEventListener('change', handleReduceMotionChange)
    isReducedMotion = mediaQuery.matches

    if (isReducedMotion && introVisualsRef.current) {
      introVisualsRef.current.classList.add('reduced-motion')
    }

    if (!isReducedMotion) {
      setAnimTrigger(true)
    }

    const monaProgress = {
      start: 0,
      end: 300,
      value: 0,
      _value: 0,
    }

    const titleProgress = {
      start: 300,
      end: 600,
      value: 0,
      _value: 0,
    }

    const cloudsProgress = {
      start: 0,
      end: 600,
      value: 0,
      _value: 0,
    }

    const time = {
      last: Date.now(),
      current: Date.now(),
      delta: 0,
    }

    const getInterpolationValue = (start: number, end: number, scroll: number) => {
      return Math.max(0.0, Math.min((scroll - start) / (end - start), 1))
    }

    const scrollFunc = () => {
      monaProgress._value = getInterpolationValue(monaProgress.start, monaProgress.end, window.scrollY)
      titleProgress._value = getInterpolationValue(titleProgress.start, titleProgress.end, window.scrollY)
      cloudsProgress._value = getInterpolationValue(cloudsProgress.start, cloudsProgress.end, window.scrollY)
    }
    scrollFunc()
    // eslint-disable-next-line github/prefer-observers
    window.addEventListener('scroll', scrollFunc)

    const tick = () => {
      if (isReducedMotion) return

      time.current = Date.now()
      time.delta = (time.current - time.last) / 1000
      time.last = time.current

      if (window.innerWidth <= 767) {
        setCopyScrollScale(1)
        setCopyScrollOpacity(1)
      } else {
        monaProgress.value += (monaProgress._value - monaProgress.value) * Math.min(1, 10 * time.delta)
        titleProgress.value += (titleProgress._value - titleProgress.value) * Math.min(1, 10 * time.delta)
        cloudsProgress.value += (cloudsProgress._value - cloudsProgress.value) * Math.min(1, 5 * time.delta)

        if (monaHeadRef.current) {
          monaHeadRef.current.style.opacity = `${1 - monaProgress.value}`
          monaHeadRef.current.style.transform = `translate(-50%, -50%) scale(${1 - 0.1 * monaProgress.value}) `
        }

        const translateX = cloudsProgress.value * 20
        const scale = 1 - 0.1 * cloudsProgress.value

        if (clouds1Ref.current) {
          clouds1Ref.current.style.transform = `translate(${-translateX}%, 0) scale(${scale})`
          clouds1Ref.current.style.opacity = `${1 - cloudsProgress.value}`
        }
        if (clouds2Ref.current) {
          clouds2Ref.current.style.transform = `translate(${translateX}%, 0) scale(${scale})`
          clouds2Ref.current.style.opacity = `${1 - cloudsProgress.value}`
        }

        setCopyScrollScale(1 - titleProgress.value * 0.1)
        setCopyScrollOpacity(1 - titleProgress.value)
      }

      animationFrameId.current = requestAnimationFrame(tick)
    }
    tick()

    const handleBeforeUnload = () => {
      cancelAnimationFrame(animationFrameId.current)
    }
    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current)
      }
      window.removeEventListener('scroll', scrollFunc)
      window.removeEventListener('beforeunload', handleBeforeUnload)
      mediaQuery.removeEventListener('change', handleReduceMotionChange)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="lp-IntroVisuals-universe" ref={introVisualsRef}>
      <div className="lp-IntroVisuals-universe-blurBg" ref={blurBgRef}>
        <img src={BlurBg} alt="" aria-hidden="true" />
      </div>
      <div className="lp-IntroVisuals-universe-blur" />
      <div className="lp-IntroVisuals-universe-clouds1" ref={clouds1Ref}>
        <PearlCloud cloudImgSrc={Clouds1} trigger={animTrigger} />
      </div>
      <div className="lp-IntroVisuals-universe-clouds2" ref={clouds2Ref}>
        <PearlCloud cloudImgSrc={Clouds2} trigger={animTrigger} />
      </div>

      <div className="lp-IntroVisuals-universe-monaHead" ref={monaHeadRef}>
        <img src={MonaHead} alt="" aria-hidden="true" />
        <HeroButterfly isAssetsLoaded={isAssetsLoaded} />
      </div>
    </div>
  )
}

try{ IntroHeroUniverse.displayName ||= 'IntroHeroUniverse' } catch {}